/**
 * Templates — May 2026 rebuild per templates_editor_v3.html.
 *
 * Layout (left → right):
 *   • Icon rail (44 px)            — +New / All / Email / SMS / Dialer / Search
 *   • Slide-out panel (320 px)     — tabs, search, grouped list
 *   • Editor (flex 1)              — header, tabs (Edit/Preview/Performance/History)
 *   • Right sidebar (260 px)       — type pill, settings toggles, recent versions
 *
 * AI is wired through the existing /api/templates/generate plus the
 * three new /api/templates/ai/{rewrite,subject-alternatives,improve-body}
 * endpoints. Every interactive element has a data-testid.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Search, Mail, MessageSquare, Phone, FileText, Filter, Settings as SettingsIcon,
  Copy, Eye, Play, Check, X, Sparkles, RefreshCw, ChevronRight, Pin, PinOff, Archive,
  ArchiveRestore, Trash2, Megaphone, Loader2, Monitor, Smartphone, Send, History,
} from "lucide-react";
import { useAuth, API } from "@/App";
import UseInCampaignSheet from "@/components/templates/UseInCampaignSheet";
import TemplatesSettings from "@/components/templates/TemplatesSettings";
import NewTemplateModal from "@/components/templates/NewTemplateModal";

// ── constants ───────────────────────────────────────────────────────
const TYPE_META = {
  email:  { Icon: Mail,          label: "Email",         color: "#0071e3" },
  sms:    { Icon: MessageSquare, label: "SMS",           color: "#10b981" },
  dialer: { Icon: Phone,         label: "Dialer script", color: "#7c3aed" },
};

const PAGE_TABS = [
  { key: "all",      label: "All" },
  { key: "email",    label: "Email" },
  { key: "sms",      label: "SMS" },
  { key: "dialer",   label: "Dialer" },
];

const STATUS_PILL = {
  published: { bg: "rgba(48,209,88,0.10)",  fg: "#1a7f3c", label: "Published" },
  draft:     { bg: "rgba(116,116,128,0.13)", fg: "#6e6e73", label: "Draft" },
  archived:  { bg: "rgba(255,59,48,0.09)",  fg: "#8b1a16", label: "Archived" },
};

// ── product-line grouping (Iter 174 — Danny redesign) ────────────────
// Iter 174.1 (Danny): multi-classify so a template tagged with RPM,
// APCM, and/or CCM lands in EACH respective group. The CCM group always
// renders even when empty; ditto for the others. RPM and APCM are now
// separate groups.
const PRODUCT_GROUPS = [
  { key: "ccm",     name: "CCM — Chronic Care Management",  dot: "#10b981", tag: "CCM",     tagBg: "rgba(16,185,129,0.15)",  tagFg: "#0a6e4f", match: (p) => p === "CCM" },
  { key: "ehr",     name: "EHR / MDNet",                    dot: "#3b82f6", tag: "EHR",     tagBg: "rgba(59,130,246,0.15)",  tagFg: "#1e40af", match: (p) => p === "EHR" || p === "MDNET" || p === "MD NET" },
  { key: "rcm",     name: "Billing / RCM",                  dot: "#f59e0b", tag: "RCM",     tagBg: "rgba(245,158,11,0.18)",  tagFg: "#92400e", match: (p) => p === "RCM" || p.includes("BILLING") },
  { key: "rpm",     name: "RPM — Remote Patient Monitoring", dot: "#8b5cf6", tag: "RPM",     tagBg: "rgba(139,92,246,0.15)",  tagFg: "#5b21b6", match: (p) => p === "RPM" },
  { key: "apcm",    name: "APCM — Advanced Primary Care Mgmt", dot: "#ec4899", tag: "APCM",  tagBg: "rgba(236,72,153,0.15)",  tagFg: "#9d174d", match: (p) => p === "APCM" },
  { key: "general", name: "General outreach",               dot: "#9ca3af", tag: "General", tagBg: "rgba(116,116,128,0.13)", tagFg: "#4b5563", match: null /* fallback */ },
];

// Returns the list of group keys this template belongs to. Multi-tagged
// templates show up in EVERY matching group; un-tagged or unrecognized
// templates fall into "general".
function classifyProductGroups(template) {
  const products = Array.isArray(template?.products) ? template.products : [];
  const hits = new Set();
  for (const raw of products) {
    const p = (raw || "").toString().trim().toUpperCase();
    if (!p) continue;
    for (const g of PRODUCT_GROUPS) {
      if (g.match && g.match(p)) hits.add(g.key);
    }
  }
  if (hits.size === 0) hits.add("general");
  return Array.from(hits);
}

// Back-compat single-product helper still used by ProductTagRow pills.
function classifyProduct(template) {
  return classifyProductGroups(template)[0] || "general";
}

function variantsForName(name, allTemplates) {
  // Cosmetic: templates with the same name (case-insensitive trimmed) are
  // treated as Email/SMS/Call-script siblings of each other for the channel
  // switcher. Returns a map { email, sms, dialer } of template or null.
  const out = { email: null, sms: null, dialer: null };
  if (!name) return out;
  const target = name.trim().toLowerCase();
  for (const t of allTemplates) {
    if ((t.name || "").trim().toLowerCase() === target) {
      if (t.type && out[t.type] === null) out[t.type] = t;
    }
  }
  return out;
}

// ── component ───────────────────────────────────────────────────────
export default function Templates() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isSettingsRoute = window.location.pathname.endsWith("/templates/settings");

  const role = (user?.role || "").toLowerCase();
  const isAdmin = ["super_admin", "crm_admin"].includes(role);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelTab, setPanelTab] = useState("all");
  const [panelOpen, setPanelOpen] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [edTab, setEdTab] = useState("edit"); // edit | preview | performance | history
  const [previewMode, setPreviewMode] = useState("desktop");
  const [showPlaceholders, setShowPlaceholders] = useState(true);
  const [useInCampaignTpl, setUseInCampaignTpl] = useState(null);
  const [showSettings, setShowSettings] = useState(isSettingsRoute);
  const [showNewModal, setShowNewModal] = useState(false);
  const [aiSubjectAlts, setAiSubjectAlts] = useState([]);
  const [aiSpamReport, setAiSpamReport] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  // ── data loading ─────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (panelTab !== "all") params.type = panelTab;
      if (query) params.q = query;
      const r = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setTemplates(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Couldn't load templates");
    } finally {
      setLoading(false);
    }
  }, [token, panelTab, query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (templateId && templateId !== "settings" && templates.length > 0 && (!selected || selected.id !== templateId)) {
      const found = templates.find((t) => t.id === templateId);
      if (found) { setSelected(found); setDraft({ ...found }); }
    }
  }, [templateId, templates]); // eslint-disable-line

  // ── CRUD ─────────────────────────────────────────────────────────
  const selectTemplate = (t) => {
    setSelected(t); setDraft({ ...t });
    setEdTab("edit"); setAiSubjectAlts([]); setAiSpamReport(null);
    navigate(`/templates/${t.id}`, { replace: true });
  };

  const startFromDraft = (d) => {
    setSelected(null); setDraft(d);
    setShowNewModal(false); setEdTab("edit");
    setAiSubjectAlts([]); setAiSpamReport(null);
    navigate("/templates", { replace: true });
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const r = draft.id
        ? await axios.put(`${API}/templates/${draft.id}`, draft, { headers })
        : await axios.post(`${API}/templates`, draft, { headers });
      toast.success(`${TYPE_META[draft.type].label} saved`);
      setSelected(r.data); setDraft(r.data);
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    if (!draft) return;
    const next = draft.status === "published" ? "draft" : "published";
    const optimistic = { ...draft, status: next };
    setDraft(optimistic);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const r = draft.id
        ? await axios.put(`${API}/templates/${draft.id}`, optimistic, { headers })
        : await axios.post(`${API}/templates`, optimistic, { headers });
      setSelected(r.data); setDraft(r.data);
      // Iter 159 (Josh — #615e8fb0 / Lisa) — clarify that "Publish" is also
      // the team-share toggle. Once published, the template appears for every
      // user in the org. Going back to "draft" hides it from teammates again.
      toast.success(next === "published" ? "Published & shared with team" : "Reverted to draft (private)");
      await load();
    } catch {
      toast.error("Publish toggle failed");
      setDraft(draft);
    }
  };

  const duplicate = async () => {
    if (!selected?.id) {
      toast.error("Save the template before duplicating");
      return;
    }
    try {
      const r = await axios.post(
        `${API}/templates/${selected.id}/duplicate?type=${selected.type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Duplicated");
      selectTemplate(r.data); await load();
    } catch (e) {
      toast.error("Duplicate failed: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const togglePin = async () => {
    if (!selected?.id) return;
    try {
      const r = await axios.post(
        `${API}/templates/${selected.id}/pin?type=${selected.type}&pinned=${!selected.pinned}`,
        {}, { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.data?.pinned ? "Pinned" : "Unpinned");
      setSelected({ ...selected, pinned: !!r.data?.pinned });
      setDraft({ ...draft, pinned: !!r.data?.pinned });
      await load();
    } catch {
      toast.error("Pin failed");
    }
  };

  const toggleArchive = async () => {
    if (!selected?.id) return;
    const goingToArchive = selected.status !== "archived";
    if (goingToArchive && !window.confirm(`Archive "${selected.name}"?`)) return;
    try {
      const r = await axios.post(
        `${API}/templates/${selected.id}/archive?type=${selected.type}&archived=${goingToArchive}`,
        {}, { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.data?.status === "archived" ? "Archived" : "Restored");
      if (goingToArchive) { setSelected(null); setDraft(null); navigate("/templates", { replace: true }); }
      await load();
    } catch {
      toast.error("Archive failed");
    }
  };

  const remove = async () => {
    if (!selected?.id) return;
    if (!window.confirm(`Delete "${selected.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/templates/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { type: selected.type },
      });
      toast.success("Deleted");
      setSelected(null); setDraft(null);
      navigate("/templates", { replace: true });
      await load();
    } catch (e) {
      // Iter 174 (Danny) — surface the actual backend reason
      // ("Only the owner or an admin can delete this template", etc.)
      // instead of the generic "Delete failed" toast.
      toast.error("Delete failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  // ── AI helpers ───────────────────────────────────────────────────
  const aiSubjectAlternatives = async () => {
    if (!draft || draft.type !== "email") return;
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/templates/ai/subject-alternatives`, {
        current_subject: draft.subject || "",
        body_preview: (draft.body || "").slice(0, 500),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAiSubjectAlts(r.data?.alternatives || []);
      toast.success(`${r.data?.alternatives?.length || 0} AI suggestions ready`);
    } catch (e) {
      toast.error("AI failed: " + (e?.response?.data?.detail || e?.message));
    } finally { setAiBusy(false); }
  };

  const aiImproveBody = async () => {
    if (!draft || !["email", "sms"].includes(draft.type)) return;
    const hint = window.prompt("Optional hint (leave blank for general polish):", "") || "";
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/templates/ai/improve-body`, {
        current_text: draft.body || "", hint, type: draft.type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const improved = r.data?.improved || "";
      if (improved) {
        setDraft({ ...draft, body: improved });
        toast.success("Body improved" + (r.data?.notes ? ` — ${r.data.notes}` : ""));
      } else {
        toast.error("AI returned no changes");
      }
    } catch (e) {
      toast.error("AI failed: " + (e?.response?.data?.detail || e?.message));
    } finally { setAiBusy(false); }
  };

  const aiRewriteFromGoal = async () => {
    if (!draft) return;
    const goal = window.prompt("Describe a change or goal — AI rewrites the body:", "");
    if (!goal) return;
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/templates/ai/rewrite`, {
        section: "body", current_text: draft.body || "", goal, type: draft.type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.rewritten) {
        setDraft({ ...draft, body: r.data.rewritten });
        toast.success("Body rewritten");
      } else { toast.error("AI returned no changes"); }
    } catch (e) {
      toast.error("AI failed: " + (e?.response?.data?.detail || e?.message));
    } finally { setAiBusy(false); }
  };

  const aiCheckSpam = async () => {
    if (!draft || !["email", "sms"].includes(draft.type)) return;
    setAiBusy(true);
    setAiSpamReport(null);
    try {
      const r = await axios.post(`${API}/templates/ai/check-spam`, {
        subject: draft.subject || "",
        body: draft.body || "",
        type: draft.type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAiSpamReport(r.data);
      const v = (r.data?.verdict || "low").toLowerCase();
      if (v === "low") toast.success(`Spam risk: low (${r.data?.score ?? 0}/100)`);
      else if (v === "medium") toast.warning(`Spam risk: medium (${r.data?.score ?? 0}/100)`);
      else toast.error(`Spam risk: HIGH (${r.data?.score ?? 0}/100)`);
    } catch (e) {
      toast.error("AI failed: " + (e?.response?.data?.detail || e?.message));
    } finally { setAiBusy(false); }
  };

  // ── derived ──────────────────────────────────────────────────────
  // Iter 174.1 (Danny): multi-classify — a template tagged with RPM,
  // APCM, AND CCM should appear in ALL THREE groups. Pinned templates
  // surface at the top of their respective group(s).
  const grouped = useMemo(() => {
    const out = {};
    for (const g of PRODUCT_GROUPS) out[g.key] = [];
    for (const t of templates) {
      for (const key of classifyProductGroups(t)) {
        (out[key] || out.general).push(t);
      }
    }
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      });
    }
    return out;
  }, [templates]);

  // Settings short-circuit
  if (showSettings) {
    return <TemplatesSettings onBack={() => { setShowSettings(false); navigate("/templates"); }} />;
  }

  return (
    <div
      data-testid="templates-page"
      style={{
        display: "flex", height: "calc(100vh - 60px)",
        background: "var(--page-bg, #f2f2f7)",
        fontFamily: '"DM Sans", -apple-system, sans-serif',
      }}
    >
      {/* ── PRODUCT TREE PANEL (Iter 174 redesign) ── */}
      <ProductTreePanel
        loading={loading}
        templates={templates}
        grouped={grouped}
        selected={selected}
        query={query}
        setQuery={setQuery}
        panelTab={panelTab}
        setPanelTab={setPanelTab}
        onSelect={selectTemplate}
        onNew={() => setShowNewModal(true)}
      />

      {/* ── EDITOR ── */}
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "var(--page-bg, #f5f5f7)" }}>
        {!draft ? (
          <EmptyEditor onNew={() => setShowNewModal(true)} />
        ) : (
          <EditorPane
            draft={draft} setDraft={setDraft}
            selected={selected}
            allTemplates={templates}
            onSelectVariant={selectTemplate}
            onNewVariant={() => setShowNewModal(true)}
            edTab={edTab} setEdTab={setEdTab}
            previewMode={previewMode} setPreviewMode={setPreviewMode}
            showPlaceholders={showPlaceholders} setShowPlaceholders={setShowPlaceholders}
            saving={saving} aiBusy={aiBusy}
            aiSubjectAlts={aiSubjectAlts} setAiSubjectAlts={setAiSubjectAlts}
            onSave={save}
            onPublishToggle={togglePublish}
            onDuplicate={duplicate}
            onUseInCampaign={() => setUseInCampaignTpl(selected || draft)}
            onPin={togglePin}
            onArchive={toggleArchive}
            onDelete={remove}
            onSettings={isAdmin ? () => { setShowSettings(true); navigate("/templates/settings"); } : null}
            aiSubjectAlternatives={aiSubjectAlternatives}
            aiImproveBody={aiImproveBody}
            aiRewriteFromGoal={aiRewriteFromGoal}
            aiCheckSpam={aiCheckSpam}
            aiSpamReport={aiSpamReport}
            clearSpamReport={() => setAiSpamReport(null)}
          />
        )}
      </main>

      {/* ── RIGHT SIDEBAR ── */}
      {draft && (
        <RightSidebar
          draft={draft} setDraft={setDraft}
          allTemplates={templates}
          onPin={togglePin}
          isAdmin={isAdmin}
        />
      )}

      {/* Modals */}
      {useInCampaignTpl && (
        <UseInCampaignSheet
          template={useInCampaignTpl}
          onClose={() => setUseInCampaignTpl(null)}
          onAttached={() => { setUseInCampaignTpl(null); load(); }}
        />
      )}
      {showNewModal && (
        <NewTemplateModal
          onClose={() => setShowNewModal(false)}
          onDraftReady={startFromDraft}
        />
      )}
    </div>
  );
}

// ── ProductTreePanel (Iter 174 redesign — left column) ────────────
function ProductTreePanel({ loading, templates, grouped, selected, query, setQuery, panelTab, setPanelTab, onSelect, onNew }) {
  const [collapsed, setCollapsed] = useState({}); // group key -> bool
  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));

  // Apply local search filter on top of the already-grouped server result
  // so the user gets instant feedback while typing without an API round-trip.
  const filterFn = (t) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (t.name || "").toLowerCase().includes(q)
      || (t.subject || "").toLowerCase().includes(q)
      || (t.description || "").toLowerCase().includes(q);
  };

  // For the variant icons next to each row — checks whether any sibling
  // template (same name) exists for the given channel.
  const variantMapByName = useMemo(() => {
    const out = new Map();
    for (const t of templates) {
      const k = (t.name || "").trim().toLowerCase();
      if (!k) continue;
      if (!out.has(k)) out.set(k, { email: false, sms: false, dialer: false });
      out.get(k)[t.type] = true;
    }
    return out;
  }, [templates]);

  return (
    <aside
      data-testid="templates-tree-panel"
      style={{
        width: 320, minWidth: 320, flexShrink: 0,
        background: "var(--surface, #fff)",
        borderRight: "0.5px solid var(--border)",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* Header */}
      <div style={{ padding: "14px 14px 10px", borderBottom: "0.5px solid var(--border-soft, rgba(0,0,0,0.05))" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Templates</div>
          <div data-testid="tpl-count" style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
            {templates.length} template{templates.length === 1 ? "" : "s"}
          </div>
        </div>
        <div style={{ position: "relative", marginTop: 9 }}>
          <Search style={{ position: "absolute", left: 8, top: 8, width: 12, height: 12, color: "var(--text-tertiary)" }} />
          <input
            id="tpl-search-input"
            data-testid="templates-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search templates…"
            style={{
              width: "100%", padding: "6px 8px 6px 26px",
              border: "0.5px solid var(--border)", borderRadius: 8,
              fontSize: 12, background: "var(--page-bg, #f5f5f7)",
              outline: "none", boxSizing: "border-box", fontFamily: "inherit",
            }}
          />
        </div>
        {/* Iter 178.3 (Lisa #161064e0 Feb 2026) — the channel filter
            tabs (All / Email / SMS / Dialer) were rendered here in
            previous iterations but disappeared during a refactor. The
            `panelTab` state + the backend `params.type=…` filter still
            existed but the UI to flip between channels was gone, so
            reps could only see all templates merged together. This
            restores the four-tab strip so reps can scope the slide-
            out panel to one channel at a time. */}
        <div
          data-testid="tpl-page-tabs"
          role="tablist"
          aria-label="Filter templates by channel"
          style={{
            display: "flex",
            gap: 4,
            marginTop: 10,
            background: "var(--page-bg, #f5f5f7)",
            padding: 3,
            borderRadius: 8,
          }}
        >
          {PAGE_TABS.map((t) => {
            const active = panelTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`tpl-page-tab-${t.key}`}
                onClick={() => setPanelTab(t.key)}
                style={{
                  flex: 1,
                  padding: "5px 8px",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--text-primary)" : "var(--text-secondary)",
                  background: active ? "var(--surface, #fff)" : "transparent",
                  boxShadow: active ? "0 0.5px 2px rgba(0,0,0,0.08)" : "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.14s ease, color 0.14s ease",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 8 }}>
        {loading && (
          <div style={{ padding: 18, textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
            <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite", verticalAlign: "middle", marginRight: 5 }} />
            Loading…
          </div>
        )}
        {!loading && templates.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: "var(--text-tertiary)" }}>
            <FileText style={{ width: 22, height: 22, opacity: 0.35, marginBottom: 6 }} />
            <div style={{ fontSize: 12, fontWeight: 500 }}>No templates yet</div>
            <button
              data-testid="empty-create"
              onClick={onNew}
              style={{
                marginTop: 8, padding: "5px 10px", background: "#0071e3", color: "#fff",
                border: "none", borderRadius: 7, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              + Create your first
            </button>
          </div>
        )}
        {!loading && PRODUCT_GROUPS.map((g) => {
          const all = grouped[g.key] || [];
          const list = all.filter(filterFn);
          // Iter 174.1 (Danny): always render every group header so
          // empty CCM/RPM/APCM aren't invisible. Hide only when the
          // user has typed a search query and this group has 0 hits.
          if (list.length === 0 && (query || "").length > 0) return null;
          const isCollapsed = !!collapsed[g.key];
          const isEmpty = list.length === 0;
          return (
            <div key={g.key} data-testid={`tpl-group-${g.key}`}>
              {/* Group header */}
              <button
                data-testid={`tpl-group-header-${g.key}`}
                onClick={() => !isEmpty && toggle(g.key)}
                disabled={isEmpty}
                style={{
                  width: "100%", display: "flex", alignItems: "center",
                  padding: "10px 14px 6px", gap: 8,
                  background: "transparent", border: "none",
                  cursor: isEmpty ? "default" : "pointer",
                  fontFamily: "inherit",
                  opacity: isEmpty ? 0.55 : 1,
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: g.dot, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)", letterSpacing: 0.1, flex: 1, textAlign: "left" }}>{g.name}</span>
                <span style={{ fontSize: 10, color: "var(--text-tertiary)", background: "var(--page-bg,#f5f5f7)", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>{list.length}</span>
                {!isEmpty && (
                  <ChevronRight
                    style={{
                      width: 12, height: 12, color: "var(--text-tertiary)",
                      transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                      transition: "transform 0.12s",
                    }}
                  />
                )}
              </button>
              {!isCollapsed && !isEmpty && list.map((t) => {
                const active = selected?.id === t.id;
                const variants = variantMapByName.get((t.name || "").trim().toLowerCase()) || { email: false, sms: false, dialer: false };
                const pub = (t.status || "draft") === "published";
                return (
                  <div
                    key={t.id}
                    data-testid={`tpl-row-${t.id}`}
                    onClick={() => onSelect(t)}
                    style={{
                      padding: "8px 14px 8px 26px", cursor: "pointer",
                      background: active ? "rgba(0,113,227,0.08)" : "transparent",
                      borderLeft: active ? "2px solid #0071e3" : "2px solid transparent",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--page-bg,#f5f5f7)"; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                  >
                    {/* mini icon */}
                    <div style={{
                      width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                      background: active ? "rgba(0,113,227,0.18)" : "var(--page-bg,#f3f4f6)",
                      color: active ? "#0071e3" : "var(--text-tertiary)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <FileText style={{ width: 11, height: 11 }} />
                    </div>
                    {/* name + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name || "(unnamed)"}</div>
                      <div style={{ fontSize: 10, marginTop: 2, display: "flex", gap: 5, alignItems: "center" }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: "50%",
                          background: pub ? "#10b981" : "#cbd5e1",
                          flexShrink: 0,
                        }} title={pub ? "Published" : "Draft"} />
                        <span style={{
                          padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 600,
                          background: g.tagBg, color: g.tagFg, textTransform: "uppercase", letterSpacing: 0.3,
                        }}>
                          {g.tag}
                        </span>
                        {t.pinned && <Pin style={{ width: 9, height: 9, color: "#8a5500" }} />}
                      </div>
                    </div>
                    {/* variant icons (email / sms / dialer) */}
                    <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                      <VariantDot Icon={Mail}          on={variants.email}  onColor="#0071e3" onBg="rgba(0,113,227,0.13)" />
                      <VariantDot Icon={MessageSquare} on={variants.sms}    onColor="#15803d" onBg="rgba(16,185,129,0.15)" />
                      <VariantDot Icon={Phone}         on={variants.dialer} onColor="#92400e" onBg="rgba(245,158,11,0.18)" />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer — New template */}
      <div style={{ padding: "8px 14px 14px", borderTop: "0.5px solid var(--border-soft, rgba(0,0,0,0.05))" }}>
        <button
          data-testid="new-template-cta"
          onClick={onNew}
          style={{
            width: "100%", padding: "8px 10px",
            background: "rgba(0,113,227,0.08)", color: "#0071e3",
            border: "0.5px dashed rgba(0,113,227,0.35)", borderRadius: 8,
            fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}
        >
          <Plus style={{ width: 12, height: 12 }} /> New template
        </button>
      </div>
    </aside>
  );
}

function VariantDot({ Icon, on, onColor, onBg }) {
  return (
    <span
      style={{
        width: 18, height: 18, borderRadius: 4,
        background: on ? onBg : "var(--page-bg,#f3f4f6)",
        color: on ? onColor : "#d1d5db",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}
      title={on ? "Has variant" : "Missing variant"}
    >
      <Icon style={{ width: 10, height: 10 }} />
    </span>
  );
}

// ── EmptyEditor (no template selected) ─────────────────────────────
function EmptyEditor({ onNew }) {
  return (
    <div
      data-testid="editor-empty"
      style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)", padding: 40, textAlign: "center" }}
    >
      <FileText style={{ width: 36, height: 36, opacity: 0.3, marginBottom: 10 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>Select a template to edit</div>
      <div style={{ fontSize: 12, marginTop: 4, maxWidth: 320 }}>
        Pick one from the left rail or create a new template — AI can draft the first version for you.
      </div>
      <button
        data-testid="editor-empty-new"
        onClick={onNew}
        style={{ marginTop: 14, padding: "8px 16px", background: "#0071e3", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "inherit" }}
      >
        <Plus style={{ width: 12, height: 12 }} /> New template
      </button>
    </div>
  );
}

// ── EditorPane ─────────────────────────────────────────────────────
function EditorPane({
  draft, setDraft, selected,
  allTemplates, onSelectVariant, onNewVariant,
  edTab, setEdTab, previewMode, setPreviewMode,
  showPlaceholders, setShowPlaceholders,
  saving, aiBusy,
  aiSubjectAlts, setAiSubjectAlts,
  onSave, onPublishToggle, onDuplicate, onUseInCampaign,
  onPin, onArchive, onDelete, onSettings,
  aiSubjectAlternatives, aiImproveBody, aiRewriteFromGoal,
  aiCheckSpam, aiSpamReport, clearSpamReport,
}) {
  const sp = STATUS_PILL[draft.status] || STATUS_PILL.draft;
  const isPublished = draft.status === "published";

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* HEADER */}
      <div style={{
        padding: "12px 18px", borderBottom: "0.5px solid var(--border)",
        background: "var(--surface, #fff)",
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <input
          data-testid="ed-name"
          value={draft.name || ""}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          style={{
            fontSize: 16, fontWeight: 600, border: "none", outline: "none",
            background: "transparent", flex: 1, minWidth: 200, fontFamily: "inherit", color: "var(--text-primary)",
          }}
        />
        <span data-testid="ed-status-pill" style={{ fontSize: 10, padding: "2px 8px", borderRadius: 12, background: sp.bg, color: sp.fg, fontWeight: 600 }}>{sp.label}</span>
        <div style={{ display: "flex", gap: 5 }}>
          <BtnGhost testid="ed-duplicate" onClick={onDuplicate}><Copy style={{ width: 12, height: 12 }} /> Duplicate</BtnGhost>
          <BtnPurple testid="ed-use-in-campaign" onClick={onUseInCampaign}><Megaphone style={{ width: 12, height: 12 }} /> Use in campaign</BtnPurple>
          <BtnPrimary testid="ed-publish-toggle" onClick={onPublishToggle}>
            {isPublished ? <X style={{ width: 12, height: 12 }} /> : <Check style={{ width: 12, height: 12 }} />}
            {isPublished ? "Unpublish" : "Publish"}
          </BtnPrimary>
          <BtnGhost testid="ed-save" onClick={onSave} disabled={saving}>
            {saving ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : null}
            {saving ? "Saving…" : "Save"}
          </BtnGhost>
          {selected?.id && (
            <BtnGhost testid="ed-archive" onClick={onArchive}>
              {draft.status === "archived" ? <ArchiveRestore style={{ width: 12, height: 12 }} /> : <Archive style={{ width: 12, height: 12 }} />}
            </BtnGhost>
          )}
          {onSettings && (
            <BtnGhost testid="ed-settings" onClick={onSettings}>
              <SettingsIcon style={{ width: 12, height: 12 }} />
            </BtnGhost>
          )}
          {selected?.id && (
            <BtnGhost testid="ed-delete" onClick={onDelete} style={{ color: "#b91c1c" }}>
              <Trash2 style={{ width: 12, height: 12 }} />
            </BtnGhost>
          )}
        </div>
      </div>

      {/* PRODUCT TAG ROW */}
      <ProductTagRow draft={draft} setDraft={setDraft} />

      {/* CHANNEL VARIANT SWITCHER (cosmetic — same-named templates) */}
      <ChannelVariantSwitcher
        draft={draft}
        allTemplates={allTemplates || []}
        onSelectVariant={onSelectVariant}
        onNewVariant={onNewVariant}
      />

      {/* TABS */}
      <div style={{ display: "flex", gap: 0, padding: "0 18px", background: "var(--surface, #fff)", borderBottom: "0.5px solid var(--border)" }}>
        {[
          { k: "edit", label: "Edit" },
          { k: "preview", label: "Preview" },
          { k: "performance", label: "Performance" },
          { k: "history", label: "Version history" },
        ].map((t) => {
          const active = edTab === t.k;
          return (
            <button
              key={t.k}
              data-testid={`ed-tab-${t.k}`}
              onClick={() => setEdTab(t.k)}
              style={{
                padding: "9px 12px", fontSize: 12, fontWeight: active ? 600 : 500,
                border: "none", background: "transparent",
                color: active ? "#0071e3" : "var(--text-secondary)",
                borderBottom: active ? "2px solid #0071e3" : "2px solid transparent",
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* BODY */}
      <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
        {edTab === "edit" && (
          <EditTab
            draft={draft} setDraft={setDraft} aiBusy={aiBusy}
            aiSubjectAlts={aiSubjectAlts} setAiSubjectAlts={setAiSubjectAlts}
            aiSubjectAlternatives={aiSubjectAlternatives}
            aiImproveBody={aiImproveBody}
            aiRewriteFromGoal={aiRewriteFromGoal}
            aiCheckSpam={aiCheckSpam}
            aiSpamReport={aiSpamReport}
            clearSpamReport={clearSpamReport}
          />
        )}
        {edTab === "preview" && (
          <PreviewTab draft={draft} previewMode={previewMode} setPreviewMode={setPreviewMode} showPlaceholders={showPlaceholders} setShowPlaceholders={setShowPlaceholders} />
        )}
        {edTab === "performance" && <PerformanceTab draft={draft} onUseInCampaign={onUseInCampaign} />}
        {edTab === "history" && <HistoryTab draft={draft} />}
      </div>
    </div>
  );
}

// ── ProductTagRow (Iter 174) ───────────────────────────────────────
// Lets the user see + edit the product tags directly under the header.
// Tags drive the left-tree grouping; adding "CCM" here moves the
// template into the CCM group on next render.
const KNOWN_PRODUCTS = ["CCM", "EHR", "RPM", "APCM", "RCM", "MDNet", "General"];

function ProductTagRow({ draft, setDraft }) {
  const [adding, setAdding] = useState(false);
  const products = Array.isArray(draft.products) ? draft.products : [];
  const removeTag = (p) => setDraft({ ...draft, products: products.filter((x) => x !== p) });
  const addTag = (p) => {
    if (!p || products.includes(p)) { setAdding(false); return; }
    setDraft({ ...draft, products: [...products, p] });
    setAdding(false);
  };
  const pillStyle = (p) => {
    const grp = classifyProduct({ products: [p] });
    const meta = PRODUCT_GROUPS.find((g) => g.key === grp) || PRODUCT_GROUPS[PRODUCT_GROUPS.length - 1];
    return { background: meta.tagBg, color: meta.tagFg };
  };
  return (
    <div
      data-testid="product-tag-row"
      style={{
        padding: "9px 18px",
        background: "var(--surface, #fff)",
        borderBottom: "0.5px solid var(--border-soft, rgba(0,0,0,0.05))",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Products:</span>
      {products.length === 0 && (
        <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontStyle: "italic" }}>None — falls into General outreach</span>
      )}
      {products.map((p) => (
        <button
          key={p}
          data-testid={`product-pill-${p.toLowerCase()}`}
          onClick={() => removeTag(p)}
          style={{
            ...pillStyle(p),
            border: "none", padding: "3px 10px", borderRadius: 12,
            fontSize: 11, fontWeight: 600, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
            fontFamily: "inherit",
          }}
          title="Click to remove"
        >
          {p} <X style={{ width: 10, height: 10 }} />
        </button>
      ))}
      {adding ? (
        <select
          data-testid="product-add-select"
          autoFocus
          defaultValue=""
          onBlur={() => setAdding(false)}
          onChange={(e) => addTag(e.target.value)}
          style={{
            fontSize: 11, padding: "3px 8px", borderRadius: 12,
            border: "0.5px solid var(--border)", background: "var(--surface,#fff)",
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <option value="" disabled>Add product…</option>
          {KNOWN_PRODUCTS.filter((p) => !products.includes(p)).map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      ) : (
        <button
          data-testid="product-add-btn"
          onClick={() => setAdding(true)}
          style={{
            background: "var(--page-bg,#f5f5f7)", border: "0.5px dashed var(--border)",
            padding: "3px 10px", borderRadius: 12,
            fontSize: 11, fontWeight: 500, color: "var(--text-secondary)",
            cursor: "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 3,
          }}
        >
          <Plus style={{ width: 10, height: 10 }} /> Add
        </button>
      )}
    </div>
  );
}

// ── ChannelVariantSwitcher (Iter 174 — cosmetic) ──────────────────
// Templates with the same name (case-insensitive) are treated as
// Email/SMS/Call-script siblings. Clicking a tab navigates to the
// matching template; if none exists, the "+ Add variant" button opens
// the New Template modal pre-filled with the same name + products.
function ChannelVariantSwitcher({ draft, allTemplates, onSelectVariant, onNewVariant }) {
  const variants = useMemo(() => variantsForName(draft.name, allTemplates), [draft.name, allTemplates]);
  const channels = [
    { key: "email",  label: "Email",       Icon: Mail,          color: "#0071e3" },
    { key: "sms",    label: "SMS",         Icon: MessageSquare, color: "#15803d" },
    { key: "dialer", label: "Call script", Icon: Phone,         color: "#92400e" },
  ];
  return (
    <div
      data-testid="channel-variant-switcher"
      style={{
        padding: "9px 18px",
        background: "var(--surface, #fff)",
        borderBottom: "0.5px solid var(--border-soft, rgba(0,0,0,0.05))",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Channel variant</span>
      {channels.map((c) => {
        const sibling = variants[c.key];
        const isCurrent = draft.type === c.key;
        const hasVariant = !!sibling;
        return (
          <button
            key={c.key}
            data-testid={`variant-tab-${c.key}`}
            onClick={() => {
              if (isCurrent) return;
              if (sibling) onSelectVariant(sibling);
              else if (onNewVariant) onNewVariant(c.key);
            }}
            disabled={!hasVariant && !onNewVariant}
            style={{
              padding: "5px 10px", borderRadius: 8,
              border: isCurrent ? `1.5px solid ${c.color}` : "0.5px solid var(--border)",
              background: isCurrent ? `${c.color}15` : (hasVariant ? "var(--surface,#fff)" : "var(--page-bg,#f5f5f7)"),
              color: isCurrent ? c.color : (hasVariant ? "var(--text-primary)" : "var(--text-tertiary)"),
              fontSize: 11.5, fontWeight: isCurrent ? 600 : 500,
              cursor: (isCurrent || (!hasVariant && !onNewVariant)) ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
              fontFamily: "inherit",
            }}
          >
            <c.Icon style={{ width: 12, height: 12 }} />
            {c.label}
            {hasVariant ? (
              <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "rgba(48,209,88,0.18)", color: "#0a6e4f", fontWeight: 600 }}>Ready</span>
            ) : (
              <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "var(--page-bg,#f3f4f6)", color: "var(--text-tertiary)", fontWeight: 600 }}>Missing</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── EditTab ─────────────────────────────────────────────────────────
function EditTab({ draft, setDraft, aiBusy, aiSubjectAlts, setAiSubjectAlts, aiSubjectAlternatives, aiImproveBody, aiRewriteFromGoal, aiCheckSpam, aiSpamReport, clearSpamReport }) {
  const applyAlt = (text) => {
    setDraft({ ...draft, subject: text });
    setAiSubjectAlts([]);
    toast.message("Subject replaced");
  };

  const verdictStyle = (v) => {
    const k = (v || "low").toLowerCase();
    if (k === "high")   return { bg: "rgba(255,59,48,0.10)",  fg: "#8b1a16", label: "High risk" };
    if (k === "medium") return { bg: "rgba(255,159,10,0.12)", fg: "#8a5500", label: "Medium risk" };
    return { bg: "rgba(48,209,88,0.10)", fg: "#1a7f3c", label: "Low risk" };
  };

  return (
    <div style={{ maxWidth: 840, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* AI quick-rewrite row */}
      <div
        style={{
          background: "rgba(139,92,246,0.05)",
          border: "0.5px solid rgba(139,92,246,0.16)",
          borderRadius: 10, padding: "10px 14px",
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}
      >
        <Sparkles style={{ width: 13, height: 13, color: "#7c3aed" }} />
        <span style={{ fontSize: 11, color: "#5b21b6", fontWeight: 500 }}>
          Describe a change or goal — AI rewrites any section
        </span>
        <div style={{ flex: 1 }} />
        {(draft.type === "email" || draft.type === "sms") && (
          <button
            data-testid="ai-check-spam"
            onClick={aiCheckSpam}
            disabled={aiBusy}
            style={btnAiMini}
            title="Check deliverability + spam-filter risk"
          >
            {aiBusy ? <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite" }} /> : "✦"} Check spam risk
          </button>
        )}
        <button
          data-testid="ai-rewrite-goal"
          onClick={aiRewriteFromGoal}
          disabled={aiBusy}
          style={btnAiMini}
        >
          {aiBusy ? <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite" }} /> : "✦"} AI rewrite body
        </button>
      </div>

      {/* Spam report card */}
      {aiSpamReport && (() => {
        const vs = verdictStyle(aiSpamReport.verdict);
        return (
          <div
            data-testid="ai-spam-report"
            style={{
              background: vs.bg, border: `0.5px solid ${vs.fg}33`,
              borderRadius: 10, padding: "10px 14px",
              display: "flex", flexDirection: "column", gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: vs.fg, padding: "2px 8px", borderRadius: 10, background: "#fff" }}>
                {vs.label} · {aiSpamReport.score ?? 0}/100
              </span>
              <span style={{ fontSize: 11, color: vs.fg, flex: 1 }}>
                AI deliverability check — higher score means more likely to land in spam.
              </span>
              <button
                data-testid="ai-spam-close"
                onClick={clearSpamReport}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: vs.fg }}
                aria-label="Dismiss"
              >
                <X style={{ width: 12, height: 12 }} />
              </button>
            </div>
            {!!(aiSpamReport.risks || []).length && (
              <ul data-testid="ai-spam-risks" style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: vs.fg, display: "flex", flexDirection: "column", gap: 2 }}>
                {aiSpamReport.risks.map((r, i) => (
                  <li key={i}><strong>{r.category}:</strong> {r.detail}</li>
                ))}
              </ul>
            )}
            {!!(aiSpamReport.suggestions || []).length && (
              <div style={{ fontSize: 11.5, color: vs.fg }}>
                <strong>Suggestions:</strong>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {aiSpamReport.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        );
      })()}

      {/* SUBJECT (email only) */}
      {draft.type === "email" && (
        <div>
          <RowLabel>
            <span>Subject line</span>
            <button
              data-testid="ai-subject-alts"
              onClick={aiSubjectAlternatives}
              disabled={aiBusy}
              style={{
                fontSize: 11, color: "#7c3aed",
                background: "transparent", border: "none", cursor: aiBusy ? "wait" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 3, fontFamily: "inherit",
              }}
            >
              {aiBusy ? <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 11, height: 11 }} />}
              Show AI alternatives
            </button>
          </RowLabel>
          <input
            data-testid="ed-subject"
            value={draft.subject || ""}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            placeholder="e.g. Are you capturing your CCM revenue, {{LastName}}?"
            style={inputStyle}
          />
          {!!aiSubjectAlts.length && (
            <div data-testid="ai-alts-list" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 5 }}>
              {aiSubjectAlts.map((alt, i) => (
                <div
                  key={i}
                  data-testid={`ai-alt-${i}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "6px 10px", borderRadius: 8,
                    background: "rgba(139,92,246,0.05)",
                    border: "0.5px solid rgba(139,92,246,0.15)",
                  }}
                >
                  <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: "rgba(139,92,246,0.15)", color: "#5b21b6", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {alt.angle}
                  </span>
                  <span style={{ flex: 1, fontSize: 12 }}>{alt.text}</span>
                  <button
                    data-testid={`ai-alt-use-${i}`}
                    onClick={() => applyAlt(alt.text)}
                    style={{ fontSize: 10, background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, padding: "3px 9px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PREVIEW TEXT (email only) */}
      {draft.type === "email" && (
        <div>
          <RowLabel><span>Preview text</span></RowLabel>
          <input
            data-testid="ed-preview-text"
            value={draft.preview_text || ""}
            onChange={(e) => setDraft({ ...draft, preview_text: e.target.value })}
            placeholder="Inbox preview (shown after the subject in email clients)…"
            style={inputStyle}
          />
        </div>
      )}

      {/* BODY (email/sms) */}
      {(draft.type === "email" || draft.type === "sms") && (
        <div>
          <RowLabel>
            <span>{draft.type === "sms" ? "Message" : "Body"}</span>
            <button
              data-testid="ai-improve-body"
              onClick={aiImproveBody}
              disabled={aiBusy}
              style={btnAiMini}
            >
              {aiBusy ? <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite" }} /> : "✦"} AI improve
            </button>
          </RowLabel>
          <textarea
            data-testid="ed-body"
            value={draft.body || ""}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            rows={draft.type === "sms" ? 4 : 14}
            placeholder={draft.type === "sms" ? "Hi {{FirstName}}, quick reminder…" : "Hi Dr. {{LastName}},\n\n…"}
            style={{ ...inputStyle, resize: "vertical", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
            maxLength={draft.type === "sms" ? 160 : undefined}
          />
          {draft.type === "sms" && (
            <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 4, textAlign: "right" }}>
              {(draft.body || "").length} / 160 chars
            </div>
          )}
        </div>
      )}

      {/* DIALER sections */}
      {draft.type === "dialer" && (
        <DialerEditor draft={draft} setDraft={setDraft} />
      )}

      {/* Merge field helpers (text reference only) */}
      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
        <strong style={{ color: "var(--text-secondary)" }}>Merge fields:</strong>{" "}
        <code style={{ background: "var(--page-bg,#f5f5f7)", padding: "1px 5px", borderRadius: 4 }}>{`{{FirstName}}`}</code>{" "}
        <code style={{ background: "var(--page-bg,#f5f5f7)", padding: "1px 5px", borderRadius: 4 }}>{`{{LastName}}`}</code>{" "}
        <code style={{ background: "var(--page-bg,#f5f5f7)", padding: "1px 5px", borderRadius: 4 }}>{`{{Company}}`}</code>{" "}
        <code style={{ background: "var(--page-bg,#f5f5f7)", padding: "1px 5px", borderRadius: 4 }}>{`{{Specialty}}`}</code>
      </div>
    </div>
  );
}

function DialerEditor({ draft, setDraft }) {
  const update = (i, patch) => {
    const next = [...(draft.sections || [])];
    next[i] = { ...next[i], ...patch };
    setDraft({ ...draft, sections: next });
  };
  const add = () => {
    setDraft({ ...draft, sections: [...(draft.sections || []), { id: crypto.randomUUID(), title: "New section", body: "" }] });
  };
  const removeSec = (i) => {
    if (!window.confirm("Remove this section?")) return;
    const next = [...(draft.sections || [])]; next.splice(i, 1);
    setDraft({ ...draft, sections: next });
  };
  return (
    <div data-testid="dialer-editor" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {(draft.sections || []).map((s, i) => (
        <div key={s.id || i} style={{ border: "0.5px solid var(--border)", borderRadius: 10, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <input
              data-testid={`dialer-sec-title-${i}`}
              value={s.title || ""}
              onChange={(e) => update(i, { title: e.target.value })}
              style={{ ...inputStyle, fontWeight: 600 }}
              placeholder="Section title (e.g. Opener)"
            />
            <BtnGhost testid={`dialer-sec-remove-${i}`} onClick={() => removeSec(i)}>
              <Trash2 style={{ width: 11, height: 11 }} />
            </BtnGhost>
          </div>
          <textarea
            data-testid={`dialer-sec-body-${i}`}
            value={s.body || ""}
            onChange={(e) => update(i, { body: e.target.value })}
            rows={5}
            placeholder="Script for this section…"
            style={{ ...inputStyle, fontFamily: "ui-monospace, Menlo, monospace" }}
          />
        </div>
      ))}
      <button
        data-testid="dialer-add-section"
        onClick={add}
        style={{
          padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
          border: "0.5px dashed var(--border)", background: "transparent",
          cursor: "pointer", fontFamily: "inherit", color: "var(--text-secondary)",
        }}
      >
        <Plus style={{ width: 11, height: 11, display: "inline", verticalAlign: -1 }} /> Add section
      </button>
    </div>
  );
}

// ── PreviewTab ─────────────────────────────────────────────────────
function PreviewTab({ draft, previewMode, setPreviewMode, showPlaceholders, setShowPlaceholders }) {
  const SAMPLE = { FirstName: "Sarah", LastName: "Mehta", Company: "Mehta Cardiology", Specialty: "Cardiology" };
  const render = (txt) => {
    if (!txt) return "";
    if (showPlaceholders) return txt;
    return txt.replace(/\{\{(\w+)\}\}/g, (_, k) => SAMPLE[k] ?? `{{${k}}}`);
  };
  // Iter 138 (martha bug 146ec3d2) — email body may contain HTML
  // (signatures like "<p>Best,<br/>{{Name}}</p>"). Render via
  // dangerouslySetInnerHTML for emails with a light allow-list scrub of
  // <script>/<iframe>/inline-event handlers so the preview matches what
  // the recipient will actually see in their inbox. SMS/dialer remain
  // text-only.
  const sanitizeHtml = (raw) => {
    if (!raw) return "";
    return String(raw)
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, "")
      .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
      .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "")
      .replace(/javascript:/gi, "");
  };
  const subject = draft.type === "email" ? render(draft.subject || "(no subject)") : "";
  const body = render(draft.body || "");
  const looksLikeHtml = /<\w+[^>]*>/.test(body);
  const emailBodyHtml = draft.type === "email"
    ? (looksLikeHtml
        ? sanitizeHtml(body)
        : sanitizeHtml(body).replace(/\n/g, "<br/>"))
    : "";

  return (
    <div style={{ maxWidth: 840, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <BtnGhost testid="preview-desktop" onClick={() => setPreviewMode("desktop")} active={previewMode === "desktop"}>
          <Monitor style={{ width: 12, height: 12 }} /> Desktop
        </BtnGhost>
        <BtnGhost testid="preview-mobile" onClick={() => setPreviewMode("mobile")} active={previewMode === "mobile"}>
          <Smartphone style={{ width: 12, height: 12 }} /> Mobile
        </BtnGhost>
        <BtnGhost testid="preview-send-test" onClick={() => toast.success("Test send queued (mock — wire to /api/integrations/m365/email/send)")}>
          <Send style={{ width: 12, height: 12 }} /> Send test email
        </BtnGhost>
        <div style={{ flex: 1 }} />
        <BtnGhost testid="preview-merge-toggle" onClick={() => setShowPlaceholders((v) => !v)}>
          {showPlaceholders ? "Show sample data" : "Show placeholders"}
        </BtnGhost>
      </div>
      <div
        data-testid="preview-frame"
        style={{
          width: previewMode === "mobile" ? 360 : "100%",
          margin: previewMode === "mobile" ? "0 auto" : 0,
          background: "#fff", border: "0.5px solid var(--border)",
          borderRadius: 10, padding: "16px 18px",
          fontSize: 12.5, lineHeight: 1.65, color: "var(--text-primary)",
        }}
      >
        {draft.type === "email" && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{subject}</div>
            <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginBottom: 12 }}>{render(draft.preview_text || "")}</div>
            {body
              ? <div data-testid="preview-email-body" dangerouslySetInnerHTML={{ __html: emailBodyHtml }} />
              : <div style={{ color: "var(--text-tertiary)", fontStyle: "italic" }}>(empty — type into the Body field on the Edit tab to see a preview here)</div>}
            {/* Block-based composer preview — shows alongside body so reps using BOTH inputs see everything */}
            {Array.isArray(draft.blocks) && draft.blocks.length > 0 && (
              <div data-testid="preview-email-blocks" style={{ marginTop: 14, paddingTop: 12, borderTop: "0.5px dashed var(--border)" }}>
                {draft.blocks.map((b, i) => (
                  <div key={b.id || i} style={{ marginBottom: 8 }}>
                    {b.type === "text" && <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(render(b.content || b.text || "")).replace(/\n/g, "<br/>") }} />}
                    {b.type === "heading" && <h3 style={{ fontSize: 16, fontWeight: 600, margin: "8px 0 4px" }}>{render(b.content || b.text || "")}</h3>}
                    {b.type === "button" && <a href={b.url || "#"} style={{ display: "inline-block", padding: "8px 16px", background: "#0071e3", color: "#fff", borderRadius: 6, textDecoration: "none", fontSize: 12, fontWeight: 500 }}>{render(b.label || "Click here")}</a>}
                    {b.type === "image" && b.url && <img src={b.url} alt="" style={{ maxWidth: "100%", borderRadius: 4 }} />}
                    {b.type === "divider" && <hr style={{ border: "none", borderTop: "0.5px solid var(--border)", margin: "12px 0" }} />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {draft.type === "sms" && (
          <div style={{ maxWidth: 280, background: "rgba(0,113,227,0.08)", borderRadius: 16, padding: "10px 14px", color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>{body || "(empty)"}</div>
        )}
        {draft.type === "dialer" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, whiteSpace: "pre-wrap" }}>
            {(draft.sections || []).map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, marginBottom: 4 }}>{s.title}</div>
                <div>{render(s.body || "")}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PerformanceTab ─────────────────────────────────────────────────
function PerformanceTab({ draft, onUseInCampaign }) {
  const [range, setRange] = useState("30d");
  const stats = draft.stats || {};
  const grid = draft.type === "email"
    ? [["Uses", stats.uses || 0], ["Opens", stats.opens || 0], ["Replies", stats.replies || 0]]
    : draft.type === "sms"
      ? [["Uses", stats.uses || 0], ["Delivered", stats.delivered || 0], ["Replies", stats.replies || 0]]
      : [["Uses", stats.uses || 0], ["Confirms", stats.confirms || 0], ["Demos booked", stats.demos_booked || 0]];
  return (
    <div data-testid="performance-tab" style={{ maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["30d", "Last 30 days"], ["90d", "Last 90 days"], ["all", "All time"]].map(([k, l]) => (
          <BtnGhost key={k} testid={`perf-range-${k}`} onClick={() => setRange(k)} active={range === k}>{l}</BtnGhost>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${grid.length}, 1fr)`, gap: 10, marginBottom: 14 }}>
        {grid.map(([label, val], i) => (
          <div key={i} style={{ background: "var(--surface,#fff)", border: "0.5px solid var(--border)", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{val}</div>
          </div>
        ))}
      </div>
      <BtnPrimary testid="perf-use-in-campaign" onClick={onUseInCampaign}>
        <Play style={{ width: 12, height: 12 }} /> Use in campaign
      </BtnPrimary>
    </div>
  );
}

// ── HistoryTab ─────────────────────────────────────────────────────
function HistoryTab({ draft }) {
  const versions = draft.versions || [];
  return (
    <div data-testid="history-tab" style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 8 }}>
      {versions.length === 0 ? (
        <div style={{ padding: 28, textAlign: "center", color: "var(--text-tertiary)" }}>
          <History style={{ width: 22, height: 22, opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: 12.5, fontWeight: 500 }}>No version history yet</div>
          <div style={{ fontSize: 11, marginTop: 3 }}>Versions snapshot on every Publish — save once to see history accumulate.</div>
        </div>
      ) : versions.map((v, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 12, border: "0.5px solid var(--border)", borderRadius: 10, background: "var(--surface,#fff)" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>v{v.version || i + 1}</div>
            <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>{v.created_at || ""} — {v.author || "—"}</div>
          </div>
          <BtnGhost testid={`history-restore-${i}`} onClick={() => toast.message("Restore not yet wired")}>Restore</BtnGhost>
        </div>
      ))}
    </div>
  );
}

// ── RightSidebar ───────────────────────────────────────────────────
function RightSidebar({ draft, setDraft, allTemplates, onPin, isAdmin }) {
  const setSetting = (key, val) => setDraft({ ...draft, [key]: val });
  const variants = useMemo(() => variantsForName(draft.name, allTemplates || []), [draft.name, allTemplates]);
  const variantRows = [
    { key: "email",  label: "Email",       Icon: Mail,          color: "#0071e3" },
    { key: "sms",    label: "SMS",         Icon: MessageSquare, color: "#15803d" },
    { key: "dialer", label: "Call script", Icon: Phone,         color: "#92400e" },
  ];
  return (
    <aside
      data-testid="templates-right-sidebar"
      style={{
        width: 260, flexShrink: 0,
        background: "var(--surface, #fff)",
        borderLeft: "0.5px solid var(--border)",
        padding: "14px 16px", overflowY: "auto",
        display: "flex", flexDirection: "column", gap: 16,
      }}
    >
      <SidebarSection title="Variants">
        {variantRows.map((v) => {
          const ready = !!variants[v.key];
          return (
            <div key={v.key} data-testid={`rs-variant-${v.key}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <v.Icon style={{ width: 13, height: 13, color: v.color }} />
                <span>{v.label}</span>
              </span>
              <span style={{
                fontSize: 10, padding: "1px 7px", borderRadius: 4,
                background: ready ? "rgba(48,209,88,0.18)" : "var(--page-bg,#f3f4f6)",
                color: ready ? "#0a6e4f" : "var(--text-tertiary)",
                fontWeight: 600,
              }}>
                {ready ? "Ready" : "Missing"}
              </span>
            </div>
          );
        })}
      </SidebarSection>

      <SidebarSection title="Settings">
        <ToggleRow testid="rs-toggle-locked"    label="Locked for agents"  on={!!draft.locked_for_agents}    onChange={(v) => setSetting("locked_for_agents", v)} />
        <ToggleRow testid="rs-toggle-required"  label="Required template"  on={!!draft.required_template}    onChange={(v) => setSetting("required_template", v)} />
        <ToggleRow testid="rs-toggle-pinned"    label="Pinned"             on={!!draft.pinned}                onChange={onPin} />
        <ToggleRow testid="rs-toggle-signature" label="Append signature"   on={draft.append_signature !== false} onChange={(v) => setSetting("append_signature", v)} />
      </SidebarSection>

      <SidebarSection title="Performance">
        <SideStat label="Uses"    value={draft.stats?.uses || 0} />
        {draft.type === "email" && <SideStat label="Open rate" value={draft.stats?.opens ? `${Math.round((draft.stats.opens / Math.max(1, draft.stats.uses)) * 100)}%` : "—"} />}
        {draft.type === "email" && <SideStat label="Reply rate" value={draft.stats?.replies ? `${Math.round((draft.stats.replies / Math.max(1, draft.stats.uses)) * 100)}%` : "—"} />}
      </SidebarSection>

      <SidebarSection title="Last updated">
        <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
          {draft.updated_at ? new Date(draft.updated_at).toLocaleString() : "Unsaved"}
        </div>
        {!isAdmin && (
          <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 6 }}>
            Tenant defaults (signature, sender) are managed by your admin.
          </div>
        )}
      </SidebarSection>
    </aside>
  );
}

function SidebarSection({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function ToggleRow({ testid, label, on, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      <button
        data-testid={testid}
        onClick={() => onChange(!on)}
        aria-label={label}
        aria-pressed={on}
        style={{
          width: 32, height: 18, borderRadius: 10,
          background: on ? "#0071e3" : "var(--border)",
          border: "none", cursor: "pointer", position: "relative",
          transition: "background 0.15s",
        }}
      >
        <span
          style={{
            position: "absolute", top: 2, left: on ? 16 : 2,
            width: 14, height: 14, borderRadius: "50%", background: "#fff",
            transition: "left 0.15s",
            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          }}
        />
      </button>
    </div>
  );
}

function SideStat({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
      <span style={{ color: "var(--text-tertiary)" }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// ── shared bits ────────────────────────────────────────────────────
function RowLabel({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 10.5, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>
      {children}
    </div>
  );
}

function BtnPrimary({ children, testid, onClick, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      style={{ background: "#0071e3", color: "#fff", border: "none", padding: "5px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 600, cursor: disabled ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit", opacity: disabled ? 0.7 : 1 }}>
      {children}
    </button>
  );
}
function BtnPurple({ children, testid, onClick, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      style={{ background: "#7c3aed", color: "#fff", border: "none", padding: "5px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 600, cursor: disabled ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit", opacity: disabled ? 0.7 : 1 }}>
      {children}
    </button>
  );
}
function BtnGhost({ children, testid, onClick, disabled, active, style = {} }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      style={{
        background: active ? "rgba(0,113,227,0.10)" : "var(--surface,#fff)",
        color: active ? "#0071e3" : "var(--text-primary)",
        border: "0.5px solid var(--border)",
        padding: "5px 10px", borderRadius: 7, fontSize: 11.5, fontWeight: 500,
        cursor: disabled ? "wait" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 4,
        fontFamily: "inherit", opacity: disabled ? 0.7 : 1, ...style,
      }}>
      {children}
    </button>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 11px",
  border: "0.5px solid var(--border)", borderRadius: 8,
  fontSize: 12.5, background: "var(--surface,#fff)",
  outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const btnAiMini = {
  fontSize: 10.5, padding: "3px 9px", borderRadius: 6,
  background: "rgba(139,92,246,0.10)", color: "#5b21b6",
  border: "0.5px solid rgba(139,92,246,0.20)",
  cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
  display: "inline-flex", alignItems: "center", gap: 3,
};
